'use client'

import React, { useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { Play } from 'lucide-react'

import type {
  AppointmentBookingBlock as AppointmentBookingBlockProps,
  Media,
} from '@/payload-types'
import { fasthand, outfit } from '@/fonts'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getOptimizedImageUrl } from '@/utilities/getOptimizedImageUrl'
import { cn } from '@/utilities/ui'
import { NavArrowIcon } from '@/components/icons/NavArrowIcon'
import { ContactFormModal } from '@/components/ContactFormModal.lazy'

// Booking calendar is a large, below-the-fold, purely-interactive widget with no
// SEO content — code-split it out of the initial bundle (heading/copy stay SSR).
const CalBookingWidget = dynamic(
  () => import('@/blocks/AppointmentBooking/CalBookingWidget').then((m) => m.CalBookingWidget),
  { ssr: false, loading: () => <div className="min-h-[420px] w-full animate-pulse rounded-lg bg-black/5" /> },
)

type Props = AppointmentBookingBlockProps & {
  className?: string
  disableInnerContainer?: boolean
}

/** Teal accents matching reference UI */
const TEAL = '#3898a4'

function isPopulatedMedia(m: number | Media | null | undefined): m is Media {
  return typeof m === 'object' && m !== null && 'id' in m
}

function mediaSrc(media: Media): string {
  const raw = media.url ?? (media.filename ? `/media/${media.filename}` : '')
  return raw ? getMediaUrl(raw) : ''
}

export const HomepageAppointmentBooking: React.FC<Props> = (props) => {
  const {
    video,
    videoCover,
    tagline,
    quote,
    signature,
    buttonText,
    className,
  } = props

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [videoStarted, setVideoStarted] = useState(false)
  const [btnHovered, setBtnHovered] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  if (!isPopulatedMedia(video)) return null
  const src = mediaSrc(video)
  if (!src) return null

  const coverSrc = isPopulatedMedia(videoCover) ? mediaSrc(videoCover) : null

  return (
    <>
      <section
        className={cn('w-full p-[20px] my-[20px] sm:px-5 md:pt-20 md:pb-10', className)}
        style={{ boxShadow: 'rgba(0, 0, 0, 0.5) 0px 0px 10px 0px' }}
      >
        <div className="max-w-[1140px] mx-auto grid w-full grid-cols-1 items-stretch gap-8 md:grid-cols-2 md:gap-8 lg:gap-10">
          {/* Left: card */}
          <div
            className="min-w-0 rounded-2xl overflow-hidden"
            style={{ boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.5)' }}
          >
            <div className="bg-white shadow-[0_5px_20px_rgba(0,0,0,0.25)] rounded-b-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-white p-[10px]">
                <h2
                  className="font-medium text-[2rem] leading-[1.2em] "
                  style={{
                    color: 'rgb(29,29,27)',
                    fontFamily: 'var(--font-outfit-family), sans-serif',
                  }}
                >
                  Craft&apos;s
                  <br />
                  Creator
                </h2>
              </div>
              <div aria-hidden className="mt-1 h-2.5 w-full bg-[#009bab]" />

              {/* Tagline strip — mobile only (inline between heading and video) */}
              {tagline?.trim() ? (
                <div className="md:hidden bg-white">
                  <p
                    className={cn(
                      outfit.className,
                      'py-2 text-center text-[19px] font-semibold text-[#222]',
                    )}
                  >
                    {tagline.trim()}
                  </p>
                  <div aria-hidden className="h-[4px] w-full" style={{ backgroundColor: TEAL }} />
                </div>
              ) : null}

              <div className="">
                <div className="p-0">
                  <div className="relative overflow-hidden">
                    <div className="relative w-full">
                      <video
                        ref={videoRef}
                        className="aspect-video h-auto w-full object-cover"
                        controls={videoStarted}
                        playsInline
                        preload="metadata"
                        src={src}
                        poster={getOptimizedImageUrl(coverSrc) || undefined}
                      >
                        Your browser does not support the video tag.
                      </video>
                      {!videoStarted && (
                        <button
                          type="button"
                          aria-label="Play video"
                          className="absolute inset-0 flex items-center justify-center cursor-pointer"
                          onClick={() => {
                            setVideoStarted(true)
                            videoRef.current?.play().catch(() => {})
                          }}
                        >
                          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/85">
                            <Play className="ml-0.5 h-5 w-5 fill-[#1d1d1b] text-[#1d1d1b]" />
                          </span>
                        </button>
                      )}
                      {/* Tagline overlay — bottom-right of video on md+ */}
                      {tagline?.trim() ? (
                        <div className="hidden md:block absolute bottom-0 right-0 bg-white">
                          <p
                            className={cn(
                              outfit.className,
                              'p-[5px] text-[19px] font-semibold text-[#222]',
                            )}
                          >
                            {tagline.trim()}
                          </p>
                          <div
                            aria-hidden
                            className="h-[4px] w-full"
                            style={{ backgroundColor: TEAL }}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Beige content */}
                <div
                  className="px-5 pb-5 pt-6 sm:px-3 sm:pb-5 sm:pt-4 overflow-hidden "
                  style={{ background: 'linear-gradient(180deg, #D2D0C3 35%, #FFFFFF 100%)' }}
                >
                  {quote?.trim() ? (
                    <p
                      className={cn(
                        outfit.className,
                        'text-left text-[24px] font-light leading-relaxed text-[#2a2a2a]',
                      )}
                    >
                      {quote.trim()}
                    </p>
                  ) : null}
                  {signature?.trim() ? (
                    <p
                      className={cn(
                        fasthand.className,
                        'mt-5 text-right text-[28px] font-light text-[#1a1a1a] sm:text-[32px]',
                      )}
                    >
                      {signature.trim()}
                    </p>
                  ) : null}
                  {buttonText?.trim() ? (
                    <div className="mt-4 flex justify-center sm:mt-6">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 border rounded-[3px] bg-white hover:bg-[#3898a4] px-9 py-3 transition-all cursor-pointer"
                          onMouseEnter={() => setBtnHovered(true)}
                          onMouseLeave={() => setBtnHovered(false)}
                          onClick={() => setModalOpen(true)}
                          style={{
                            borderWidth: '2px 2px 10px 2px',
                            borderColor: btnHovered ? '#84FFDD' : TEAL,
                            fontFamily: 'var(--font-outfit-family), sans-serif',
                            fontWeight: 500,
                            fontSize: '21px',
                            lineHeight: '21px',
                            color: btnHovered ? 'rgb(255,255,255)' : 'rgb(29,29,27)',
                          }}
                        >
                          {buttonText.trim()}
                          <NavArrowIcon className="h-5 w-5 shrink-0" />
                        </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Right: booking widget (hidden until Cal.com is configured) */}
          <div className="min-h-[320px] min-w-0 md:min-h-[480px] hidden">
            <CalBookingWidget />
          </div>
        </div>
      </section>

      <ContactFormModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}
